import { useMemo } from 'react';
import { create } from '@signals/app-shell/shims/zustand';
import pathfinderApi from '../services/pathfinderApi';

/**
 * Shared "narrative" + map HUD state.
 *
 * Story Mode's computed timeline / AI summary and Sentinel's chat history
 * live here so the map (notably in fullscreen, where the side panels are
 * hidden) can show and extend the same content through the floating HUD
 * panels (PathfinderMapNarrativeOverlay) and the legend.
 */

export interface StoryEvent {
  id: string;
  /** 'camera' entries exist only in the fullscreen HUD sequence (camera passes merged in). */
  type: 'travel' | 'dwell' | 'camera';
  startTime: Date;
  endTime: Date;
  entityId: string;
  deviceType: string;
  /**
   * Stable 1-based number in the full HUD sequence (dwells, travel legs and
   * camera passes together). Survives cropping to the map view so the HUD,
   * map badges and AI summary always agree.
   */
  seq?: number;
  // Camera-specific
  cameraId?: string;
  category?: string | null;
  distanceM?: number;
  hits?: number;
  // Travel-specific
  distance?: number;
  pointCount?: number;
  startLocation?: [number, number];
  endLocation?: [number, number];
  tripCount?: number;
  // Dwell-specific
  location?: [number, number];
  duration?: number;
  address?: string;
  visitCount?: number;
}

/** Current map viewport (degrees), published by the map on every move. */
export interface MapViewBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** Is a [lat, lng] inside the map view (antimeridian-safe)? */
export const inView = (b: MapViewBounds | null | undefined, ll?: [number, number]): boolean => {
  if (!b) return false;
  if (!ll || !Number.isFinite(ll[0]) || !Number.isFinite(ll[1])) return false;
  const [lat, lng] = ll;
  if (lat < b.south || lat > b.north) return false;
  // Viewports crossing the antimeridian have west > east.
  return b.west <= b.east ? lng >= b.west && lng <= b.east : lng >= b.west || lng <= b.east;
};

/**
 * Story events that fall inside the map view. Dwells use their location;
 * travel legs count when either end is on screen. Numbering (#1, #2 …)
 * follows this list, so the HUD, map badges and AI summary stay in step.
 */
export function filterStoryEventsToView(events: StoryEvent[], bounds: MapViewBounds | null, crop: boolean): StoryEvent[] {
  if (!crop || !bounds || events.length === 0) return events;
  const out = events.filter((e) =>
    e.type === 'travel' ? inView(bounds, e.startLocation) || inView(bounds, e.endLocation) : inView(bounds, e.location),
  );
  return out.length === events.length ? events : out;
}

/**
 * The fullscreen HUD sequence: Story events plus camera passes (when the
 * camera layer is on and the user has not hidden them), time-ordered and
 * numbered 1..N. The left-panel Story Mode keeps its own dwell/travel list.
 */
export function buildStorySequence(
  events: StoryEvent[],
  entities: string[],
  cameras: StoryCameraContext | null,
  includeCameras: boolean,
): StoryEvent[] {
  const passes = includeCameras && cameras?.layerOn && cameras.passed.length > 0 ? cameras.passed : [];
  if (passes.length === 0) {
    return events.map((e, i) => (e.seq === i + 1 ? e : { ...e, seq: i + 1 }));
  }
  const wanted = new Set(entities);
  let mine = wanted.size > 0 ? passes.filter((p) => wanted.has(p.entityId)) : passes;
  if (mine.length === 0) mine = passes; // entity ids keyed differently - keep them all rather than drop them
  const camEvents: StoryEvent[] = mine
    .map((p, i): StoryEvent | null => {
      const start = new Date(p.time);
      const end = new Date(p.endTime || p.time);
      if (!Number.isFinite(start.getTime())) return null;
      return {
        id: `cam-${p.cameraId ?? p.seq}-${p.entityId}-${i}`,
        type: 'camera' as const,
        startTime: start,
        endTime: Number.isFinite(end.getTime()) ? end : start,
        entityId: p.entityId,
        deviceType: '',
        location: [p.lat, p.lng] as [number, number],
        address: p.label,
        cameraId: p.cameraId,
        category: p.category ?? null,
        distanceM: p.distanceM,
        hits: p.hits,
      };
    })
    .filter((e): e is StoryEvent => e !== null);
  const merged = [...events, ...camEvents].sort(
    (a, b) => a.startTime.getTime() - b.startTime.getTime() || (a.type === 'camera' ? 1 : 0) - (b.type === 'camera' ? 1 : 0),
  );
  return merged.map((e, i) => ({ ...e, seq: i + 1 }));
}

export interface NarrativeChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  /** Opaque action descriptors from Sentinel (kept so history survives remounts). */
  actions?: any[];
}

/** Floating HUD panels that share style + layout state. */
export type HudPanelId = 'story' | 'ai' | 'sentinel' | 'legend';
/** Panels that can be opened / closed and docked (the legend is always available). */
export type HudDockablePanelId = 'story' | 'ai' | 'sentinel';
/** Dark, low-saturation presets (Endurion brand first); 'paper' is for print-style readability. */
export type HudTheme = 'endurion' | 'tactical' | 'slate' | 'paper';
export const HUD_THEME_IDS: HudTheme[] = ['endurion', 'tactical', 'slate', 'paper'];
/** Floating card vs. a strip docked along the bottom of the map. */
export type HudLayout = 'float' | 'dock';
export interface HudPosition {
  left: number;
  top: number;
}

export interface HudStyle {
  theme: HudTheme;
  /** Panel width in px (story / sentinel). */
  width: number;
  /** Background opacity 0.4–1. */
  opacity: number;
  /** Text scale 0.85–1.3. */
  fontScale: number;
  /** Accent color (hex). */
  accent: string;
  /** Show the stats strip above the story timeline. */
  showStats: boolean;
  /** Docked (horizontal) story timeline format. */
  storyFormat: StoryTimelineFormat;
  /** User-resized heights (px). Float 0 = automatic. */
  storyDockHeight: number;
  storyFloatHeight: number;
  /** Same for the separate AI summary card. */
  aiDockHeight: number;
  aiFloatHeight: number;
  /** Preferred AI summary length. */
  storyAiDetail: StoryAiDetail;
}

export type StoryAiDetail = 'short' | 'detailed';

export const STORY_DOCK_HEIGHT_MIN = 180;
export const STORY_FLOAT_HEIGHT_MIN = 220;

/** Cameras along the loaded track, published by the map (route-sequence pass detection). */
export interface StoryCameraContext {
  /** Cameras in the catalog (after category/source filters). */
  loadedCount: number;
  /** Whether the camera layer itself is switched on (cameras only join the HUD sequence then). */
  layerOn?: boolean;
  /** Cameras within the near-track radius (same set used for pass detection). */
  nearCount: number;
  passedCount: number;
  radiusM: number;
  passed: Array<{
    seq: number;
    entityId: string;
    cameraId?: string;
    label: string;
    category?: string | null;
    time: string;
    endTime: string;
    lat: number;
    lng: number;
    distanceM?: number;
    hits?: number;
  }>;
}

/**
 * Horizontal timeline formats for the docked Story card:
 *  - zigzag: markers on a centre rail, cards alternate above/below
 *  - ledger: one column per event, labelled rows (segment / duration / window / where / track)
 *  - stops:  tall dwell tiles joined by compact travel legs, with a time-share bar
 */
export type StoryTimelineFormat = 'zigzag' | 'ledger' | 'stops';
export const STORY_TIMELINE_FORMATS: Array<{ id: StoryTimelineFormat; label: string; hint: string }> = [
  { id: 'zigzag', label: 'Zig-zag', hint: 'Alternating cards on a centre rail' },
  { id: 'ledger', label: 'Ledger', hint: 'Columns per event with labelled rows' },
  { id: 'stops', label: 'Stops & legs', hint: 'Dwell tiles joined by travel legs' },
];

/** Accent swatches: Endurion red / maroon / steel, then military olive, amber, khaki, white. */
export const HUD_ACCENTS = ['#FF2332', '#8A2C34', '#AEB7C2', '#7C8F3A', '#D4891B', '#C2A878', '#FFFFFF'] as const;

export const HUD_FONT_SCALE_MIN = 0.85;
export const HUD_FONT_SCALE_MAX = 1.6;

export const DEFAULT_HUD_STYLE: HudStyle = {
  theme: 'endurion',
  width: 360,
  opacity: 0.92,
  fontScale: 1,
  accent: HUD_ACCENTS[0],
  showStats: true,
  storyFormat: 'zigzag',
  storyDockHeight: 272,
  storyFloatHeight: 0,
  aiDockHeight: 272,
  aiFloatHeight: 0,
  storyAiDetail: 'short',
};

const STYLE_KEY = 'pathfinder.hud.style';

const loadStyle = (): HudStyle => {
  try {
    const raw = localStorage.getItem(STYLE_KEY);
    if (!raw) return DEFAULT_HUD_STYLE;
    const parsed = { ...DEFAULT_HUD_STYLE, ...JSON.parse(raw) } as HudStyle;
    // Older builds stored themes / accents that no longer exist.
    if (!HUD_THEME_IDS.includes(parsed.theme)) parsed.theme = DEFAULT_HUD_STYLE.theme;
    if (!(HUD_ACCENTS as readonly string[]).includes(parsed.accent)) parsed.accent = DEFAULT_HUD_STYLE.accent;
    parsed.fontScale = Math.min(HUD_FONT_SCALE_MAX, Math.max(HUD_FONT_SCALE_MIN, Number(parsed.fontScale) || 1));
    if (!STORY_TIMELINE_FORMATS.some((f) => f.id === parsed.storyFormat)) parsed.storyFormat = DEFAULT_HUD_STYLE.storyFormat;
    parsed.storyDockHeight = Math.max(STORY_DOCK_HEIGHT_MIN, Number(parsed.storyDockHeight) || DEFAULT_HUD_STYLE.storyDockHeight);
    parsed.storyFloatHeight = Math.max(0, Number(parsed.storyFloatHeight) || 0);
    parsed.aiDockHeight = Math.max(STORY_DOCK_HEIGHT_MIN, Number(parsed.aiDockHeight) || DEFAULT_HUD_STYLE.aiDockHeight);
    parsed.aiFloatHeight = Math.max(0, Number(parsed.aiFloatHeight) || 0);
    if (parsed.storyAiDetail !== 'short' && parsed.storyAiDetail !== 'detailed') parsed.storyAiDetail = DEFAULT_HUD_STYLE.storyAiDetail;
    return parsed;
  } catch {
    return DEFAULT_HUD_STYLE;
  }
};

const saveStyle = (style: HudStyle) => {
  try {
    localStorage.setItem(STYLE_KEY, JSON.stringify(style));
  } catch {
    /* ignore quota / private mode */
  }
};

type SetterArg<T> = T | ((prev: T) => T);

interface NarrativeState {
  storyEvents: StoryEvent[];
  storyEntities: string[];
  storyAiSummary: string | null;
  /** Focus instruction the current summary was generated with (null = general summary). */
  storyAiFocus: string | null;
  storyAiLoading: boolean;
  /** Cameras passed along the track, published by the map while the Story panel is open. */
  storyCameras: StoryCameraContext | null;
  setStoryCameras: (ctx: StoryCameraContext | null) => void;
  /** Map viewport; the Story HUD / badges / AI summary crop to it when storyCropToView is on. */
  storyViewBounds: MapViewBounds | null;
  setStoryViewBounds: (b: MapViewBounds | null) => void;
  storyCropToView: boolean;
  setStoryCropToView: (v: boolean) => void;
  /** Include camera passes in the HUD sequence (when the camera layer is on). */
  storyShowCameras: boolean;
  setStoryShowCameras: (v: boolean) => void;
  /** What the current AI summary covered, e.g. "6 of 10 events in map view". */
  storyAiScope: string | null;

  sentinelMessages: NarrativeChatMessage[];
  sentinelLoading: boolean;

  /** Which narrative panels are open on the map (one, the other, or both). */
  panelsOpen: Record<HudDockablePanelId, boolean>;
  panelMinimized: Record<HudPanelId, boolean>;
  /** User-dragged positions (px, relative to the map container); null = default anchor. */
  panelPositions: Record<HudPanelId, HudPosition | null>;
  /** Floating card or docked strip along the bottom of the map. */
  panelLayout: Record<HudDockablePanelId, HudLayout>;
  hudStyle: HudStyle;
  /** Show numbered story-event badges (#1, #2 …) on the map while the Story panel is open. */
  storyMapLabels: boolean;
  setStoryMapLabels: (v: boolean) => void;

  setStoryEvents: (events: StoryEvent[], entities: string[]) => void;
  setStoryAiSummary: (summary: string | null) => void;
  setStoryAiLoading: (v: boolean) => void;

  setSentinelMessages: (v: SetterArg<NarrativeChatMessage[]>) => void;
  setSentinelLoading: (v: boolean) => void;

  setPanelOpen: (id: HudDockablePanelId, open: boolean) => void;
  togglePanel: (id: HudDockablePanelId) => void;
  setPanelMinimized: (id: HudPanelId, v: boolean) => void;
  setPanelPosition: (id: HudPanelId, pos: HudPosition | null) => void;
  setPanelLayout: (id: HudDockablePanelId, layout: HudLayout) => void;
  resetPanelPositions: () => void;
  updateHudStyle: (patch: Partial<HudStyle>) => void;
  /** Nudge the text scale (A− / A+). */
  stepFontScale: (delta: number) => void;
  resetHudStyle: () => void;
}

const NO_POSITIONS: Record<HudPanelId, HudPosition | null> = { story: null, ai: null, sentinel: null, legend: null };
const DEFAULT_LAYOUT: Record<HudDockablePanelId, HudLayout> = { story: 'float', ai: 'float', sentinel: 'float' };

export const useNarrativeStore = create<NarrativeState>((set) => ({
  storyEvents: [],
  storyEntities: [],
  storyAiSummary: null,
  storyAiFocus: null,
  storyAiLoading: false,
  storyCameras: null,
  setStoryCameras: (ctx) => set({ storyCameras: ctx }),
  storyViewBounds: null,
  setStoryViewBounds: (b) => set({ storyViewBounds: b }),
  storyCropToView: true,
  setStoryCropToView: (v) => set({ storyCropToView: v }),
  storyShowCameras: true,
  setStoryShowCameras: (v) => set({ storyShowCameras: v }),
  storyAiScope: null,
  sentinelMessages: [],
  sentinelLoading: false,

  panelsOpen: { story: false, ai: false, sentinel: false },
  panelMinimized: { story: false, ai: false, sentinel: false, legend: false },
  panelPositions: NO_POSITIONS,
  panelLayout: DEFAULT_LAYOUT,
  hudStyle: loadStyle(),
  storyMapLabels: true,
  setStoryMapLabels: (v) => set({ storyMapLabels: v }),

  setStoryEvents: (events, entities) =>
    set((s) => {
      // Entity change invalidates the previous AI summary.
      const sameEntities =
        entities.length === s.storyEntities.length && entities.every((e, i) => e === s.storyEntities[i]);
      return {
        storyEvents: events,
        storyEntities: entities,
        storyAiSummary: sameEntities ? s.storyAiSummary : null,
        storyAiFocus: sameEntities ? s.storyAiFocus : null,
        storyAiScope: sameEntities ? s.storyAiScope : null,
      };
    }),
  setStoryAiSummary: (summary) => set({ storyAiSummary: summary }),
  setStoryAiLoading: (v) => set({ storyAiLoading: v }),

  setSentinelMessages: (v) =>
    set((s) => ({ sentinelMessages: typeof v === 'function' ? v(s.sentinelMessages) : v })),
  setSentinelLoading: (v) => set({ sentinelLoading: v }),

  setPanelOpen: (id, open) =>
    set((s) => ({
      panelsOpen: { ...s.panelsOpen, [id]: open },
      panelMinimized: { ...s.panelMinimized, [id]: false },
    })),
  togglePanel: (id) =>
    set((s) => ({
      panelsOpen: { ...s.panelsOpen, [id]: !s.panelsOpen[id] },
      panelMinimized: { ...s.panelMinimized, [id]: false },
    })),
  setPanelMinimized: (id, v) => set((s) => ({ panelMinimized: { ...s.panelMinimized, [id]: v } })),
  setPanelPosition: (id, pos) => set((s) => ({ panelPositions: { ...s.panelPositions, [id]: pos } })),
  setPanelLayout: (id, layout) => set((s) => ({ panelLayout: { ...s.panelLayout, [id]: layout } })),
  resetPanelPositions: () => set({ panelPositions: NO_POSITIONS, panelLayout: DEFAULT_LAYOUT }),
  updateHudStyle: (patch) =>
    set((s) => {
      const next = { ...s.hudStyle, ...patch };
      saveStyle(next);
      return { hudStyle: next };
    }),
  stepFontScale: (delta) =>
    set((s) => {
      const fontScale = Math.round(
        Math.min(HUD_FONT_SCALE_MAX, Math.max(HUD_FONT_SCALE_MIN, s.hudStyle.fontScale + delta)) * 100,
      ) / 100;
      const next = { ...s.hudStyle, fontScale };
      saveStyle(next);
      return { hudStyle: next };
    }),
  resetHudStyle: () => {
    saveStyle(DEFAULT_HUD_STYLE);
    set({ hudStyle: DEFAULT_HUD_STYLE });
  },
}));

const fmtAiWhen = (d: Date) =>
  d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const fmtAiDur = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${Math.max(0, Math.round(seconds))}s`;
};
const fmtAiPlace = (ll?: [number, number]) =>
  ll && Number.isFinite(ll[0]) && Number.isFinite(ll[1]) ? `${ll[0].toFixed(3)}, ${ll[1].toFixed(3)}` : undefined;

/** Request body for the Story Mode AI endpoint, shared by the panel and the overlay. */
export function buildStoryAiRequest(events: StoryEvent[], entities: string[]) {
  const travelEvents = events.filter((e) => e.type === 'travel');
  const dwellEvents = events.filter((e) => e.type === 'dwell');
  const cameraEvents = events.filter((e) => e.type === 'camera');
  const secs = (e: StoryEvent) => (e.endTime.getTime() - e.startTime.getTime()) / 1000;
  return {
    entities,
    totalEvents: events.length,
    travelCount: travelEvents.length,
    dwellCount: dwellEvents.length,
    cameraPassCount: cameraEvents.length,
    totalTravelDistance: travelEvents.reduce((sum, e) => sum + (e.distance || 0), 0),
    totalTravelTime: travelEvents.reduce((sum, e) => sum + secs(e), 0),
    totalDwellTime: dwellEvents.reduce((sum, e) => sum + secs(e), 0),
    uniqueLocations: Array.from(
      new Set(dwellEvents.map((e) => e.address).filter((a): a is string => Boolean(a))),
    ),
    timelineEvents: events.map((e, i) => ({
      seq: e.seq ?? i + 1,
      type: e.type,
      entityId: e.entityId,
      deviceType: e.deviceType,
      startTime: e.startTime.toISOString(),
      endTime: e.endTime.toISOString(),
      when: `${fmtAiWhen(e.startTime)} – ${fmtAiWhen(e.endTime)}`,
      duration: secs(e),
      durationLabel: fmtAiDur(secs(e)),
      distance: e.distance,
      distanceMi: e.distance != null ? Number((e.distance * 0.621371).toFixed(2)) : undefined,
      address: e.address,
      place: e.address || fmtAiPlace(e.location),
      from: e.type === 'travel' ? fmtAiPlace(e.startLocation) : undefined,
      to: e.type === 'travel' ? fmtAiPlace(e.endLocation) : undefined,
      visits: e.visitCount,
      trips: e.tripCount,
      pointCount: e.pointCount,
      cameraId: e.cameraId,
      cameraCategory: e.type === 'camera' ? e.category ?? undefined : undefined,
      cameraDistanceM: e.distanceM,
      cameraHits: e.hits,
    })),
  };
}

/** Full HUD sequence (numbered, cameras merged) - plain-function form. */
export function getStorySequence(): StoryEvent[] {
  const s = useNarrativeStore.getState();
  return buildStorySequence(s.storyEvents, s.storyEntities, s.storyCameras, s.storyShowCameras);
}

/** Hook form of getStorySequence (memoised). */
export function useStorySequence(): StoryEvent[] {
  const events = useNarrativeStore((s) => s.storyEvents);
  const entities = useNarrativeStore((s) => s.storyEntities);
  const cameras = useNarrativeStore((s) => s.storyCameras);
  const showCameras = useNarrativeStore((s) => s.storyShowCameras);
  return useMemo(() => buildStorySequence(events, entities, cameras, showCameras), [events, entities, cameras, showCameras]);
}

/** HUD sequence cropped to the map view (numbers preserved) - plain-function form. */
export function getVisibleStoryEvents(): StoryEvent[] {
  const s = useNarrativeStore.getState();
  return filterStoryEventsToView(getStorySequence(), s.storyViewBounds, s.storyCropToView);
}

/** Hook form of getVisibleStoryEvents; memoised so consumers get a stable array. */
export function useVisibleStoryEvents(): StoryEvent[] {
  const sequence = useStorySequence();
  const bounds = useNarrativeStore((s) => s.storyViewBounds);
  const crop = useNarrativeStore((s) => s.storyCropToView);
  return useMemo(() => filterStoryEventsToView(sequence, bounds, crop), [sequence, bounds, crop]);
}

/**
 * Generate (or regenerate) the Story Mode AI summary for the events currently
 * in the store (cropped to the map view when that option is on). `focus` steers the summary (e.g. "how many cameras did they
 * pass"); `context` carries the other datasets loaded on the map. Cameras
 * passed are merged in from the store (published by the map).
 */
async function waitForStoryCameras(ms = 2500) {
  const start = Date.now();
  while (Date.now() - start < ms) {
    const cameras = useNarrativeStore.getState().storyCameras;
    if (cameras && (cameras.loadedCount > 0 || cameras.passedCount > 0)) return cameras;
    await new Promise((r) => setTimeout(r, 200));
  }
  return useNarrativeStore.getState().storyCameras;
}

export async function generateStoryAiSummary(opts: { focus?: string | null; context?: Record<string, any>; detail?: StoryAiDetail } = {}): Promise<void> {
  const { storyEntities, storyAiLoading, hudStyle, storyCropToView, storyViewBounds } = useNarrativeStore.getState();
  const allEvents = getStorySequence();
  const storyEvents = filterStoryEventsToView(allEvents, storyViewBounds, storyCropToView);
  const cameraCount = allEvents.filter((e) => e.type === 'camera').length;
  if (storyAiLoading || storyEvents.length === 0) return;
  const cropped = storyEvents.length !== allEvents.length;
  const scope = cropped ? `${storyEvents.length} of ${allEvents.length} events in map view` : `${storyEvents.length} events`;
  const focus = opts.focus?.trim() || null;
  const detail = opts.detail === 'short' || opts.detail === 'detailed' ? opts.detail : hudStyle.storyAiDetail;
  const wantsCameras = /camera/i.test(focus || '');
  useNarrativeStore.setState({ storyAiLoading: true, storyAiFocus: focus, storyAiScope: scope });
  try {
    const storyCameras = wantsCameras
      ? (await waitForStoryCameras()) ?? useNarrativeStore.getState().storyCameras
      : useNarrativeStore.getState().storyCameras;
    const context: Record<string, any> = { ...(opts.context ?? {}) };
    if (storyCameras) {
      // Crop the cameras-passed list to the map view as well, so the model never
      // sees passes the analyst has zoomed away from.
      const passed = cropped
        ? storyCameras.passed.filter((p) => inView(storyViewBounds, [p.lat, p.lng]))
        : storyCameras.passed;
      context.cameras = {
        ...storyCameras,
        passed,
        passedCount: passed.length,
        ...(cropped ? { passedTotal: storyCameras.passed.length, note: 'Only camera passes inside the current map view are listed.' } : {}),
      };
    }
    context.sequence = {
      note:
        cameraCount > 0
          ? `Timeline events are numbered 1..${allEvents.length} in time order and the numbers match the badges on the map. Camera passes (type "camera", ${cameraCount} of them) are part of that numbering alongside dwells and travel legs - cite them as "#<seq>".`
          : `Timeline events are numbered 1..${allEvents.length} in time order and the numbers match the badges on the map - cite them as "#<seq>".`,
      cameraPassesInSequence: cameraCount,
    };
    if (cropped) {
      context.view = {
        ...(context.view ?? {}),
        note: `The analyst has zoomed the map; every dataset below is cropped to the current map view, and only ${storyEvents.length} of the ${allEvents.length} numbered events are inside it. Numbers are kept from the full sequence (they are not renumbered), so gaps are expected.`,
        eventsInView: storyEvents.length,
        eventsTotal: allEvents.length,
      };
    }
    const result = await pathfinderApi.generateStoryModeAI({
      ...buildStoryAiRequest(storyEvents, storyEntities),
      focus: focus ?? undefined,
      context: Object.keys(context).length ? context : undefined,
      detail,
    });
    useNarrativeStore.setState({ storyAiSummary: result.summary });
  } catch (error: any) {
    console.error('Failed to generate Story Mode AI summary:', error);
    const detail = error?.response?.data?.detail ?? error?.message;
    const hint = typeof detail === 'string' && detail.trim() ? detail.trim() : 'Please try again.';
    useNarrativeStore.setState({
      storyAiSummary: `AI analysis failed. ${hint.length > 240 ? `${hint.slice(0, 240)}…` : hint}`,
    });
  } finally {
    useNarrativeStore.setState({ storyAiLoading: false });
  }
}

/**
 * Ask Sentinel a question: appends the user turn, runs the supplied response
 * generator (from usePathfinderSentinelEngine), and appends the answer. Used
 * by both the left-panel chat and the map HUD so history stays in one place.
 */
export async function askSentinel(
  question: string,
  generateResponse: (q: string) => Promise<{ content: string; actions: any[] }>,
): Promise<void> {
  const q = question.trim();
  const { sentinelLoading } = useNarrativeStore.getState();
  if (!q || sentinelLoading) return;
  const { setSentinelMessages } = useNarrativeStore.getState();
  setSentinelMessages((prev) => [...prev, { role: 'user', content: q, timestamp: new Date() }]);
  useNarrativeStore.setState({ sentinelLoading: true });
  try {
    // Small "thinking" delay keeps the UX consistent with the panel.
    await new Promise((resolve) => setTimeout(resolve, 500 + Math.random() * 500));
    const { content, actions } = await generateResponse(q);
    setSentinelMessages((prev) => [...prev, { role: 'assistant', content, timestamp: new Date(), actions }]);
  } catch (error) {
    console.error('Sentinel failed to answer:', error);
    setSentinelMessages((prev) => [
      ...prev,
      { role: 'assistant', content: 'Sentinel could not analyze that. Please try again.', timestamp: new Date() },
    ]);
  } finally {
    useNarrativeStore.setState({ sentinelLoading: false });
  }
}
